import axios from "axios";
import React, { useContext, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { log } from "../../commonfunctions/logger";
import sortSizes from "../../commonfunctions/size-sorter";
import { AlertType, SalesStatus } from "../../interfaces/enums";
import { AlertContent, ProductColor, ProductDetailResponse } from "../../interfaces/interfaces";
import { FetchStatus } from "../../interfaces/types";
import { updateCurrentOrder } from "../../redux/actions/orders";
import { RootState } from "../../redux/store";
import Alert from "../Alert/Alert";
import AuthenticationHOC from "../AuthenticationHOC/AuthenticationHOC";
import CartItemchecker from "../CartItemChecker/CartItemChecker";
import { Container, StyledInput } from "../styled/styled-components";
import Toast from "../Toast/Toast";
import ColorSelector from "./ColorSelector/ColorSelector";
import { ColorSizeStock } from "./interfaces";
import SizeDetailTable from "./SizeDetailTable/SizeDetailTable";
import { Context, IContext } from "../../context/ContextApp";
import Button, { ButtonType } from "../Button/Button";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ShoppingCartWhite from "../../assets/shoppingCartWhite";
import Check from "../../assets/check";
import cx from "classnames";

interface ItemPriceDetails {
  price: string;
  currency: string;
}

interface Props {
  generateAuthHeader: any;
  product: ProductDetailResponse;
  animateCTA?: boolean;
}

const OrderForm = (props: Props) => {
  const { generateAuthHeader, product, animateCTA = false } = props;
  const [orderReference, setOrderReference] = useState("");
  const [skuFetchStatus, setSkuFetchStatus] = useState<FetchStatus>(null);
  const [inputRefs, setInputRefs] = useState<Record<string, number>>({});
  const [currentColorSizeStocks, setCurrentColorSizeStocks] = useState<ColorSizeStock[]>([]);
  const sales_statuses = [SalesStatus.Clearance, SalesStatus.Discontinued];

  const [itemPriceDetails, setItemPriceDetails] = useState<ItemPriceDetails | null>(null);

  const [shouldRenderSizeDetail, setShouldRenderSizeDetail] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [actionStatus, setActionStatus] = useState<AlertContent | null>({
    message: "Please select a colour to load stock level.",
    type: AlertType.Info,
  });

  const [currentSelectedColor, setCurrentSelectedColor] = useState<ProductColor>(
    {} as ProductColor,
  );
  const [toast, setToast] = useState<AlertContent | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const { appBranch }: IContext = useContext(Context);
  const isStockListMode = localStorage.getItem("stocklist_mode") === "true";

  const currentOrder = useSelector((state: RootState) => state.orders.currentOrder);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const getStock = async (color: any) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    let skus = "";

    if (color.sizes.length > 0) {
      setCurrentSelectedColor(color);
      setShouldRenderSizeDetail(false);
      setCurrentColorSizeStocks([]);

      for (let i = 0; i < color.sizes.length; i++) {
        skus += "&skus[]=" + color.sizes[i].sku;
      }
    } else {
      setActionStatus({
        message: `No Skus found for colour ${color.name}.`,
        type: AlertType.Error,
      });
    }
    setSkuFetchStatus("loading");
    setActionStatus(null);
    const url = `${process.env.REACT_APP_HALFBACK_ROOT_API}/orders/current_order/items/sizes_detail_by_skus?color_id=${color.id}${skus}`;

    try {
      const res = await fetch(url, { ...generateAuthHeader(), signal });
      if (res.ok) {
        const data = await res.json();
        if (!signal.aborted) {
          if (data.items) {
            const sortedStock = sortSizes(data.items);

            setItemPriceDetails({
              price: data.price,
              currency: data.currency,
            });

            setShouldRenderSizeDetail(true);
            setCurrentColorSizeStocks(sortedStock);
            const resetRefs = sortedStock.reduce(
              (acc: Record<string, number>, cs: { sku: string }) => ({ ...acc, [cs.sku]: 0 }),
              {},
            );
            setInputRefs(resetRefs);
            setSkuFetchStatus("success");
            // Now scroll the table into view
            const table = document.getElementById("size-detail-table");
            table?.scrollIntoView({ behavior: "smooth" });
          } else {
            setActionStatus({
              message: `Not able to load stock for ${color.name}. Please try again.`,
              type: AlertType.Error,
            });
            setSkuFetchStatus("fail");
          }
        }
      } else {
        const error = await res.text();
        throw error;
      }
    } catch (error) {
      if (signal.aborted) return;
      log(error, `Failed to load stock for ${color.name}`);
      const errorString = String(error);
      if (errorString.indexOf("401") !== -1) {
        alert("Your session has expired, please log in again.");
        window.location.reload();
      } else {
        setActionStatus({
          message: `Not able to load stock for ${color.name}. Please try again.`,
          type: AlertType.Error,
        });
        setSkuFetchStatus("fail");
      }
    }
  };

  const checkCartLimit = (items: ColorSizeStock[]) => {
    const cart = currentOrder.order_items;
    const res = [];
    for (let i = 0; i < cart.length; i++) {
      for (let p = 0; p < items.length; p++) {
        if (cart[i].sku === items[p].sku) {
          res.push(cart[i].sku);
        }
      }
    }
    if (cart.length + (items.length - res.length) > 150) {
      return false;
    } else {
      setShowLimitMessage(false);
      return true;
    }
  };

  const saveToCart = () => {
    if (isAddingToCart) return;
    setAddToCartSuccess(false);
    setIsAddingToCart(true);
    const items: any[] = [];
    const submittedStocks = currentColorSizeStocks;

    submittedStocks.forEach((cs) => {
      if (cs.quantity && cs.quantity > 0 && !cs.errorMessage) {
        items.push({
          sku: cs.sku,
          quantity: cs.quantity,
          itemNo: cs.formattedItemNo,
          product_id: product.id,
          color: currentSelectedColor.name,
          images: currentSelectedColor.images,
          size: cs.size,
          price: itemPriceDetails?.price,
          backorder_qty: cs.backOrderQty ?? 0,
          stock: cs.stock,
          // Fix this later - not sure of typeof etaDate
          eta: cs.etaDate,
          brand_name: product.brand,
          product_code: product.code,
          product_name: product.name,
          product_slug: product.slug,
          enable_ship_from_au: currentSelectedColor.enable_ship_from_au,
        });
      }
    });

    if (items.length < 1) {
      setIsAddingToCart(false);
      return; //if no items, break
    }

    const flag = checkCartLimit(items);

    if (flag) {
      const req_url = `${process.env.REACT_APP_HALFBACK_ROOT_API}/orders/current_order/items/create`;
      const params = {
        items,
        reference: orderReference,
      };

      axios
        .post(req_url, params, generateAuthHeader())
        .then((response) => {
          setToast({
            message: "Item added to cart.",
            type: AlertType.Success,
          });
          dispatch(updateCurrentOrder(response.data));
          setAddToCartSuccess(true);

          submittedStocks.forEach((cs) => {
            setInputRefs((inputRefs) => ({
              ...inputRefs,
              [cs.sku]: 0,
            }));
          });

          setCurrentColorSizeStocks((prevStocks) => {
            if (prevStocks !== submittedStocks) return prevStocks;

            submittedStocks.forEach((cs) => {
              cs.backOrderQty = 0;
              cs.errorMessage = "";
              cs.etaDate = "";
              cs.quantity = 0;
            });
            return [...submittedStocks];
          });
          setOrderReference("");
          setTimeout(() => {
            setIsAddingToCart(false);
            setAddToCartSuccess(false);
          }, 1200);
        })
        .catch((error) => {
          if (error.response && error.response.status === "401") {
            alert("Your session is expired. Please login again.");
            navigate("/");
          }
          setToast({
            message: "Could not add item to cart.",
            type: AlertType.Error,
          });
          setIsAddingToCart(false);
        });
    } else {
      setShowLimitMessage(true);
      setIsAddingToCart(false);
    }
  };

  const renderAddButton = () => {
    if (currentColorSizeStocks.length > 0) {
      return (
        <div className="add-to-cart-container">
          <div>
            <StyledInput
              type="text"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setOrderReference(event.target.value)
              }
              value={orderReference}
              placeholder="Enter Order Reference"
              maxLength={30}
            />
            <Button
              type={ButtonType.primary}
              onClick={() => saveToCart()}
              disabled={isAddingToCart}
              className={cx(
                animateCTA && "product-cta--attention",
                animateCTA && isAddingToCart && "product-cta--loading",
                animateCTA && addToCartSuccess && "product-cta--success",
              )}
            >
              {addToCartSuccess ? (
                <span className="add-to-cart-button__content">
                  <Check width={18} height={18} />
                  <span className="label">Added</span>
                </span>
              ) : (
                <span className="add-to-cart-button__content">
                  <ShoppingCartWhite width={18} height={18} />
                  <span className="label">Add to Cart</span>
                </span>
              )}
            </Button>
          </div>
          <small style={{ color: "gray" }}>
            *NOTE: Stock availability may have changed by the time you submit the order.
          </small>
        </div>
      );
    }
  };

  const renderAddToCartInfo = () => {
    if (showLimitMessage) {
      return <Alert>You reached the maximum limit of lines.</Alert>;
    }
  };

  const renderOrderSummary = () => {
    if (!itemPriceDetails || isStockListMode) return null;
    const unitPrice = parseFloat(itemPriceDetails.price);
    const totalQty = currentColorSizeStocks.reduce((sum, cs) => sum + (inputRefs[cs.sku] || 0), 0);
    if (totalQty === 0) return null;
    const total = totalQty * unitPrice;
    return (
      <div style={{ margin: "0.5rem 0 1rem 0", fontSize: "1em" }}>
        {totalQty} item{totalQty !== 1 ? "s" : ""} × {unitPrice.toFixed(2)} ={" "}
        <b>
          {itemPriceDetails.currency} ${total.toFixed(2)}
        </b>
      </div>
    );
  };

  return (
    <Container padding="0 0 3em 0">
      {actionStatus && <Alert content={actionStatus} />}
      <Container row nowrap={product.colors.length < 3} style={{ width: "100%" }}>
        <ColorSelector
          product={product}
          skuFetchStatus={skuFetchStatus}
          getStock={getStock}
          currentSelectedColor={currentSelectedColor}
        />
      </Container>{" "}
      {!isStockListMode && itemPriceDetails && (
        <div style={{ margin: "2em 0 0.5rem 0", fontSize: "1em", fontWeight: "bold" }}>
          {itemPriceDetails.currency} ${parseFloat(itemPriceDetails.price).toFixed(2)}
        </div>
      )}
      {skuFetchStatus === "loading" && <LoadingSpinner block />}
      <Container margin="0 0 1em 0" style={{ overflowX: "auto", width: "100%" }}>
        {
          <SizeDetailTable
            shouldRenderSizeDetail={shouldRenderSizeDetail}
            currentSelectedColor={currentSelectedColor}
            currentColorSizeStocks={currentColorSizeStocks}
            itemPrice={itemPriceDetails?.price}
            setActionStatus={setActionStatus}
            product={product}
            setCurrentColorSizeStocks={setCurrentColorSizeStocks}
            inputRefs={inputRefs}
            setInputRefs={setInputRefs}
          />
        }
      </Container>
      {itemPriceDetails && (
        <>
          {sales_statuses.includes(product.sales_status) ? (
            <Alert>
              This is a clearance/discontinued product. Back order is <b>NOT</b> available.
            </Alert>
          ) : (
            appBranch === "nz" && (
              <Alert>
                Back orders are processed by our CS team and stock shipped from alternate warehouses
                if available. For urgent orders please contact our CS team directly.
              </Alert>
            )
          )}
          {renderOrderSummary()}

          {!isStockListMode && (
            <Container row style={{ width: "100%" }}>
              {renderAddButton()}
            </Container>
          )}
          {!isStockListMode && <Container>{renderAddToCartInfo()}</Container>}
          <Container row margin="1em 0" style={{ width: "100%", overflowX: "auto" }}>
            <CartItemchecker slug={product.slug} />
          </Container>
        </>
      )}
      <Toast content={toast} onClick={() => setToast(null)} />
    </Container>
  );
};

export default AuthenticationHOC(OrderForm);
